import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { useSearchParams } from "react-router-dom";
import {
  AlertCircle,
  ExternalLink,
  RefreshCw,
  Download,
  Image as ImageIcon,
} from "lucide-react";
import TableSkeleton from "../components/skeletons/TableSkeleton";
import { InlineLoadSkeleton } from "../components/common/InlineLoadSkeleton";
import { Button } from "../components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Input } from "../components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { defectService } from "../services/defectService";
import { integrationService } from "../services/integrationService";
import PushNewDefectModal from "../components/defects/PushNewDefectModal";
import toast from "react-hot-toast";
import defectHero from "../assets/auth/defect.png";
import defectExplainIcon from "../assets/auth/defectIcon.svg";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from "recharts";
import { getMockDefectsList, getMockStatusCounts } from "../data/mockDefects";
import { getInitials } from "../utils/formatters";

const STATUS_COLORS = {
  Open: "#94a3b8",
  "In Progress": "#f59e0b",
  Reopened: "#ef4444",
  Resolved: "#b45309",
  Closed: "#22c55e",
  Deleted: "#475569",
};

const DEFECT_TABLE_HEAD_CELL =
  "border-b border-r border-border bg-[#F9F9F8] p-3 text-left font-heading text-sm font-semibold text-[#645E5B] last:border-r-0";
const DEFECT_TABLE_BODY_CELL =
  "border-b border-r border-border bg-white p-3 last:border-r-0";

const CHART_TOOLBAR_CONTROL =
  "h-9 shrink-0 rounded-md border border-solid border-[#E3E1DF] bg-white font-sans text-sm font-medium leading-[22px] text-[#1C1918] opacity-100 shadow-none hover:bg-white hover:text-[#1C1918] focus-visible:ring-0 focus-visible:ring-offset-0 whitespace-nowrap";

const CHART_TOOLBAR_SELECT =
  `${CHART_TOOLBAR_CONTROL} w-auto min-w-[7.5rem] sm:min-w-[9rem] [&>svg]:shrink-0 [&>svg]:text-[#1C1918]`;

const CHART_TOOLBAR_SEARCH =
  "h-9 min-w-[8rem] flex-1 rounded-md border border-solid border-[#E3E1DF] bg-white px-3 font-sans text-sm font-medium leading-[22px] text-[#1C1918] opacity-100 shadow-none hover:bg-white hover:text-[#1C1918] focus-visible:ring-0 focus-visible:ring-offset-0 placeholder:font-sans placeholder:text-sm placeholder:font-medium placeholder:leading-[22px] placeholder:text-[#1C1918]/50";

const CHART_TOOLBAR_ACTION_BTN =
  "h-9 shrink-0 rounded-md border-0 bg-[#A17561] font-sans text-sm font-medium leading-[22px] text-white opacity-100 shadow-none hover:border-0 hover:bg-[#825E4E] hover:text-white focus-visible:ring-0 focus-visible:ring-offset-0 whitespace-nowrap";

function getDefectReporterName(defect) {
  return (
    defect.reporter_display_name ||
    defect.reporter_full_name ||
    defect.reporter_email ||
    defect.reporter_id ||
    null
  );
}

function getDefectReporterAvatar(defect) {
  const url =
    defect.reporter_profile_image ||
    defect.reporter_avatar_url ||
    defect.reporter_avatar ||
    defect.added_by_profile_image ||
    defect.added_by?.profile_image ||
    defect.reporter?.profile_image;
  return typeof url === "string" && url.trim() ? url.trim() : null;
}

const CHART_STATUS_ORDER = [
  "Reopened",
  "Resolved",
  "In Progress",
  "Open",
  "Closed",
];

function DefectPieTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const item = payload[0];
  const name = item.name;
  const value = item.value;
  const color = item.payload?.fill ?? item.color;

  return (
    <div className="rounded-md border bg-white px-3 py-2 text-base shadow-md">
      <div className="flex items-center gap-2">
        <span
          className="h-3 w-3 shrink-0 rounded-full"
          style={{ backgroundColor: color }}
        />
        <span className="font-medium">
          {name}: {value}
        </span>
      </div>
    </div>
  );
}

function StatusBarRow({ statuses, countByStatus, maxCount }) {
  const gridClass =
    statuses.length === 3
      ? "grid-cols-1 sm:grid-cols-3"
      : "grid-cols-1 sm:grid-cols-2";

  return (
    <div className={`grid gap-3 ${gridClass}`}>
      {statuses.map((name) => {
        const count = countByStatus[name] ?? 0;
        const color = STATUS_COLORS[name] ?? "#94a3b8";
        const widthPct = (count / maxCount) * 100;

        return (
          <div key={name} className="min-w-0">
            <div className="mb-1">
              <span className="font-sans text-[20px] font-bold leading-[30px] text-[#1C1918]">
                {name}
              </span>
            </div>
            <div className="w-28 max-w-full">
              <div
                className="box-border h-3 rounded-full border-[3px] border-solid transition-all"
                style={{
                  width: count > 0 ? `${widthPct}%` : 0,
                  minWidth: count > 0 ? "12px" : 0,
                  backgroundColor: color,
                  borderColor: color,
                  transform: "rotate(0deg)",
                }}
              />
            </div>
            <p className="mt-1 font-heading text-[12px] font-normal leading-[20px] text-[#989492]">
              Defect {count}
            </p>
          </div>
        );
      })}
    </div>
  );
}

/** Raw Jira issue object from integration API */
function extractJiraIssueRoot(jiraRes) {
  return jiraRes?.issue ?? jiraRes?.data?.issue ?? null;
}

/** Normalize integration GET issue response to Jira `fields` object */
function extractJiraIssueFields(jiraRes) {
  const issue = extractJiraIssueRoot(jiraRes);
  if (!issue) return null;
  if (issue.fields && typeof issue.fields === "object") return issue.fields;
  return issue;
}

function DefectsPage() {
  const [searchParams] = useSearchParams();
  const projectId = searchParams.get("project_id");
  const clientId = searchParams.get("client_id");
  const useMockDefects =
    process.env.NODE_ENV === "development" && searchParams.get("mock") !== "0";
  const [defects, setDefects] = useState([]);
  const [statusCounts, setStatusCounts] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pushModalOpen, setPushModalOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [assigneeFilter, setAssigneeFilter] = useState("");
  const [sort, setSort] = useState("created_at");
  const [order, setOrder] = useState("desc");
  const [jiraBaseUrl, setJiraBaseUrl] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const chartContainerRef = useRef(null);

  const countByStatus = useMemo(() => {
    const map = Object.fromEntries(CHART_STATUS_ORDER.map((name) => [name, 0]));
    statusCounts.forEach(({ name, value }) => {
      if (name in map) map[name] = value;
    });
    return map;
  }, [statusCounts]);

  const pieData = useMemo(
    () =>
      CHART_STATUS_ORDER.map((name) => ({
        name,
        value: countByStatus[name] ?? 0,
      })).filter((d) => d.value > 0),
    [countByStatus],
  );

  const maxChartCount = useMemo(
    () => Math.max(1, ...CHART_STATUS_ORDER.map((s) => countByStatus[s] ?? 0)),
    [countByStatus],
  );

  const defectQueryParams = useMemo(() => {
    const params = {
      client_id: clientId,
      project_id: projectId,
      page: 1,
      limit: 50,
      sort,
      order,
    };
    if (query) params.query = query;
    if (statusFilter) params.status = statusFilter;
    if (assigneeFilter) params.assignee_id = assigneeFilter;
    return params;
  }, [clientId, projectId, sort, order, query, statusFilter, assigneeFilter]);

  useEffect(() => {
    if (!clientId) return;
    integrationService
      .getIntegration(clientId)
      .then((config) => {
        const domain =
          config?.atlassian_domain || config?.data?.atlassian_domain;
        setJiraBaseUrl(
          domain
            ? `https://${domain.replace(/^https?:\/\//, "")}/browse`
            : null,
        );
      })
      .catch(() => setJiraBaseUrl(null));
  }, [clientId]);

  const loadDefects = useCallback(async () => {
    if (!clientId || !projectId) return;
    setLoading(true);
    try {
      const params = {
        ...defectQueryParams,
      };

      if (useMockDefects) {
        const data = getMockDefectsList(params);
        setDefects(data.defects);
        setTotal(data.total ?? 0);
        return;
      }

      const res = await defectService.list(params);
      const data = res?.data ?? res;
      let rows = data?.defects ?? [];

      // Backfill Jira assignee display name when we only stored opaque accountId (assignee_id).
      const needsAssigneeName = rows.filter(
        (d) =>
          d.tool === "Jira" &&
          d.status !== "Deleted" &&
          (d.jira_issue_id || d.external_id) &&
          !String(d.external_assignee_name || "").trim() &&
          !String(d.external_assignee_email || "").trim() &&
          String(d.assignee_id || d.external_assignee_id || "").trim(),
      );
      if (needsAssigneeName.length > 0) {
        const byId = new Map(rows.map((d) => [d.id, { ...d }]));
        for (const d of needsAssigneeName) {
          const issueRef = d.jira_issue_id || d.external_id;
          try {
            const jiraRes = await integrationService.getJiraIssue(
              issueRef,
              clientId,
            );
            const root = extractJiraIssueRoot(jiraRes);
            const fields = extractJiraIssueFields(jiraRes);
            const a = fields?.assignee;
            const jiraAssigneeName = a?.displayName || a?.name || null;
            const jiraAssigneeEmail = a?.emailAddress || null;
            const jiraAssigneeId =
              a?.accountId || d.assignee_id || d.external_assignee_id || null;
            const patch = {
              client_id: clientId,
              project_id: projectId,
            };
            if (root?.key) patch.external_id = String(root.key).trim();
            if (root?.id != null) patch.jira_issue_id = String(root.id);
            if (jiraAssigneeName || jiraAssigneeEmail) {
              patch.external_assignee_name = jiraAssigneeName;
              patch.external_assignee_email = jiraAssigneeEmail;
              patch.external_assignee_id = jiraAssigneeId;
            }
            if (Object.keys(patch).length > 2) {
              await defectService.update(d.id, patch);
            }
            const cur = byId.get(d.id);
            if (cur) {
              if (patch.external_id) cur.external_id = patch.external_id;
              if (patch.jira_issue_id) cur.jira_issue_id = patch.jira_issue_id;
              if (jiraAssigneeName || jiraAssigneeEmail) {
                Object.assign(cur, {
                  external_assignee_name: jiraAssigneeName,
                  external_assignee_email: jiraAssigneeEmail,
                  external_assignee_id: jiraAssigneeId,
                  jira_assignee_display_name:
                    jiraAssigneeName || jiraAssigneeEmail || "Unassigned",
                });
              }
            }
          } catch (err) {
            // Jira returns 404 for deleted issues and for inaccessible keys (same message).
            if (err.response?.status === 404 || err.response?.status === 410) {
              try {
                await defectService.update(d.id, {
                  client_id: clientId,
                  project_id: projectId,
                  status: "Deleted",
                  external_status:
                    "Issue deleted in Jira or no longer accessible",
                });
                const cur = byId.get(d.id);
                if (cur) {
                  Object.assign(cur, {
                    status: "Deleted",
                    external_status:
                      "Issue deleted in Jira or no longer accessible",
                  });
                }
              } catch {
                /* skip */
              }
            }
          }
        }
        rows = rows.map((d) => byId.get(d.id) || d);
      }

      setDefects(rows);
      setTotal(data?.total ?? 0);
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to load defects");
      setDefects([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [clientId, projectId, defectQueryParams, useMockDefects]);

  const loadStatusCounts = useCallback(async () => {
    if (!clientId || !projectId) return;
    try {
      if (useMockDefects) {
        const data = getMockStatusCounts();
        setStatusCounts(
          data.counts.map((c) => ({
            name: c.status,
            value: parseInt(c.count, 10),
          })),
        );
        return;
      }

      const res = await defectService.statusCounts({
        client_id: clientId,
        project_id: projectId,
      });
      const data = res?.data ?? res;
      const counts = data?.counts ?? [];
      setStatusCounts(
        counts.map((c) => ({ name: c.status, value: parseInt(c.count, 10) })),
      );
    } catch {
      setStatusCounts([]);
    }
  }, [clientId, projectId, useMockDefects]);

  useEffect(() => {
    loadDefects();
  }, [loadDefects]);

  useEffect(() => {
    loadStatusCounts();
  }, [loadStatusCounts]);

  const handlePushSuccess = () => {
    loadDefects();
    loadStatusCounts();
  };

  const mapJiraStatusToInternal = (jiraStatus) => {
    if (!jiraStatus) return "Open";
    const s = String(jiraStatus).toLowerCase();
    if (s === "deleted") return "Deleted";
    if (s === "done" || s === "closed") return "Closed";
    if (s === "in progress" || s === "inprogress") return "In Progress";
    if (s === "reopened") return "Reopened";
    if (s === "resolved") return "Resolved";
    return jiraStatus;
  };

  const handleSync = async () => {
    if (!clientId || !projectId) return;
    setSyncing(true);
    try {
      const res = await defectService.list({
        client_id: clientId,
        project_id: projectId,
        limit: 100,
      });
      const data = res?.data ?? res;
      const list = data?.defects ?? [];
      const jiraDefects = list.filter(
        (d) =>
          d.tool === "Jira" &&
          (d.jira_issue_id || d.external_id) &&
          d.status !== "Deleted",
      );
      let updated = 0;
      for (const d of jiraDefects) {
        const issueRef = d.jira_issue_id || d.external_id;
        try {
          const jiraRes = await integrationService.getJiraIssue(
            issueRef,
            clientId,
          );
          const root = extractJiraIssueRoot(jiraRes);
          const fields = extractJiraIssueFields(jiraRes);
          if (!fields) continue;
          const jiraStatus = fields.status?.name || fields.status;
          const a = fields.assignee;

          const payload = {
            client_id: clientId,
            project_id: projectId,
          };
          if (root?.key) payload.external_id = String(root.key).trim();
          if (root?.id != null) payload.jira_issue_id = String(root.id);
          if (jiraStatus) {
            payload.external_status = jiraStatus;
            payload.status = mapJiraStatusToInternal(jiraStatus);
          }
          if (a) {
            payload.external_assignee_name = a.displayName || a.name || null;
            payload.external_assignee_email = a.emailAddress || null;
            payload.external_assignee_id = a.accountId || null;
            payload.assignee_id = a.accountId || null;
          } else {
            payload.external_assignee_name = null;
            payload.external_assignee_email = null;
            payload.external_assignee_id = null;
            payload.assignee_id = null;
          }

          await defectService.update(d.id, payload);
          updated += 1;
        } catch (err) {
          if (err.response?.status === 404 || err.response?.status === 410) {
            try {
              await defectService.update(d.id, {
                client_id: clientId,
                project_id: projectId,
                status: "Deleted",
                external_status:
                  "Issue deleted in Jira or no longer accessible",
              });
              updated += 1;
            } catch {
              /* skip */
            }
          }
        }
      }
      await loadDefects();
      await loadStatusCounts();
      toast.success(
        updated > 0
          ? `Synced ${updated} defect(s) from Jira`
          : "Jira sync completed",
      );
    } catch (e) {
      toast.error(e.response?.data?.message || "Sync failed");
    } finally {
      setSyncing(false);
    }
  };

  const getJiraIssueUrl = (externalId) => {
    if (!externalId || !jiraBaseUrl) return null;
    if (!/^[A-Za-z]+-\d+$/.test(String(externalId).trim())) return null;
    return `${jiraBaseUrl}/${encodeURIComponent(String(externalId).trim())}`;
  };

  if (!projectId || !clientId) {
    return (
      <div className="p-6">
        <p className="text-muted-foreground">
          Select a project to view defects.
        </p>
      </div>
    );
  }

  const hasDefects = total > 0;

  const downloadBlob = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleExportCsv = async () => {
    if (!clientId || !projectId) return;
    setExportingCsv(true);
    try {
      const response = await defectService.exportCsv(defectQueryParams);
      const today = new Date().toISOString().slice(0, 10);
      downloadBlob(response.data, `defects-export-${today}.csv`);
      toast.success("Defect list exported as CSV");
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to export CSV");
    } finally {
      setExportingCsv(false);
    }
  };

  const handleExportChartJpeg = async () => {
    try {
      const chartEl = chartContainerRef.current;
      const svgEl = chartEl?.querySelector("svg");
      if (!svgEl) {
        toast.error("No chart available to export");
        return;
      }
      const serializer = new XMLSerializer();
      const svgString = serializer.serializeToString(svgEl);
      const svgBlob = new Blob([svgString], {
        type: "image/svg+xml;charset=utf-8",
      });
      const svgUrl = URL.createObjectURL(svgBlob);
      const img = new window.Image();
      const bounds = svgEl.getBoundingClientRect();
      const width = Math.max(900, Math.round(bounds.width || 900));
      const height = Math.max(500, Math.round(bounds.height || 500));
      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
        img.src = svgUrl;
      });
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(0, 0, width, height);
      ctx.drawImage(img, 0, 0, width, height);
      URL.revokeObjectURL(svgUrl);
      const jpegBlob = await new Promise((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.92),
      );
      if (!jpegBlob) {
        toast.error("Failed to export chart");
        return;
      }
      const today = new Date().toISOString().slice(0, 10);
      downloadBlob(jpegBlob, `defect-chart-${today}.jpeg`);
      toast.success("Chart exported as JPEG");
    } catch {
      toast.error("Failed to export chart");
    }
  };

  return (
    <div className="flex min-h-[calc(100dvh-4rem-1px)] min-w-0 w-full max-w-full flex-col bg-[#F5F4F3FF] p-4 sm:p-6">
      <div className="mb-4 flex shrink-0 flex-col gap-4 border-b border-[#E5E7EBFF] pb-4 sm:mb-6 sm:flex-row sm:items-center sm:justify-between sm:pb-4">
        <h1 className="text-xl font-bold sm:text-2xl">Defects</h1>

        {/* <div className="flex flex-wrap gap-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={handleSync}
            disabled={syncing || !clientId || !projectId}
          >
            {syncing ? (
              <InlineLoadSkeleton className="h-4 w-4 mr-2" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Sync Defects
          </Button>
          <Button
            onClick={() => setPushModalOpen(true)}
            className="bg-red-600 hover:bg-red-700 text-white"
          >
            <Plus className="h-4 w-4 mr-2" />
            Push New Defect
          </Button>
        </div> */}
      </div>

      {!hasDefects && !loading && (
        <div className="flex min-h-0 flex-1 flex-col">
          <div className="flex min-h-0 flex-1 flex-col justify-top px-4 py-4 sm:px-6 sm:py-4">
            <div className="grid min-h-0 w-full grid-cols-1 gap-4 md:auto-rows-[514px] md:grid-cols-3 md:gap-6">
              <div className="flex min-h-0 flex-col items-center justify-center overflow-y-auto rounded-lg border border-solid border-[#E5E7EBFF] bg-[#E3E1DFFF] p-4 text-center sm:p-6 md:col-span-2 md:h-full md:max-h-[514px] md:p-8">
                <div className="mb-2 flex h-24 w-24 shrink-0 items-center justify-center sm:h-28 sm:w-28">
                  <img
                    src={defectHero}
                    alt=""
                    className="h-full w-full object-contain"
                  />
                </div>
                <h2 className="mb-2 text-lg font-semibold sm:text-xl">
                  No defects Yet
                </h2>
                <p className="mb-4 max-w-sm text-sm text-muted-foreground">
                  A defect is a bug found while executing a test case or
                  validating a functionality. Use below button to Create.
                </p>
                <Button
                  onClick={() => setPushModalOpen(true)}
                  className="bg-red-600 text-white hover:bg-red-700"
                >
                  Push New Defects
                </Button>
              </div>
              <div className="flex min-h-0 flex-col items-center justify-center gap-3 overflow-y-auto rounded-lg border border-border bg-muted/30 p-4 sm:p-6 md:h-full md:max-h-[514px] md:justify-center">
                <div className="flex shrink-0 items-center justify-center">
                  <img
                    src={defectExplainIcon}
                    alt=""
                    className="h-20 w-20 object-contain sm:h-24 sm:w-24"
                  />
                </div>
                <div className="shrink-0 text-center">
                  <h3 className="mb-2 text-base font-semibold sm:text-lg">
                    What are Defects?
                  </h3>
                </div>
                <p className="max-w-none text-center text-xs text-muted-foreground sm:text-sm">
                  Defects are issues identified during the execution of
                  individual test cases or while testing a specific
                  functionality, where the actual behavior of the system does
                  not match the expected behavior.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {hasDefects ? (
        <>
          {/* <div className="flex flex-wrap items-center gap-3 mb-4">
            <Input
              placeholder="Search defect..."
              className="max-w-[200px]"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && loadDefects()}
            />
            <Button variant="outline" size="sm" onClick={loadDefects}>
              Search
            </Button>
          </div> */}

          <div className="mb-6 border rounded-lg p-4" ref={chartContainerRef}>
            <div className="mb-4 flex w-full min-w-0 flex-nowrap items-center gap-4 overflow-x-auto bg-[#F9F9F8FF] pb-1 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              <Button variant="outline" className={CHART_TOOLBAR_CONTROL}>
                Assign To:{" "}
              </Button>
              <Select
                value={`${sort}-${order}`}
                onValueChange={(v) => {
                  const [s, o] = v.split("-");
                  setSort(s);
                  setOrder(o);
                }}
              >
                <SelectTrigger className={CHART_TOOLBAR_SELECT}>
                  <SelectValue placeholder="Sort" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="created_at-desc">Most Recent</SelectItem>
                  <SelectItem value="created_at-asc">Oldest</SelectItem>
                  <SelectItem value="title-asc">Title A-Z</SelectItem>
                  <SelectItem value="title-desc">Title Z-A</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={statusFilter || "_all"}
                onValueChange={(v) => setStatusFilter(v === "_all" ? "" : v)}
              >
                <SelectTrigger className={CHART_TOOLBAR_SELECT}>
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">All statuses</SelectItem>
                  {Object.keys(STATUS_COLORS).map((s) => (
                    <SelectItem key={s} value={s}>
                      {s}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" className={CHART_TOOLBAR_CONTROL}>
                Date
              </Button>

              <Input
                placeholder="Search defect..."
                className={CHART_TOOLBAR_SEARCH}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && loadDefects()}
              />

              <Button
                className={CHART_TOOLBAR_ACTION_BTN}
                onClick={handleExportCsv}
                disabled={exportingCsv}
              >
                {exportingCsv ? (
                  <InlineLoadSkeleton className="mr-2 h-4 w-4" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                CSV
              </Button>
              <Button
                className={CHART_TOOLBAR_ACTION_BTN}
                onClick={handleExportChartJpeg}
              >
                <Download className="mr-2 h-4 w-4" />
                IMG
              </Button>
            </div>

            <div className="flex flex-col gap-6 sm:flex-row sm:items-center sm:gap-8">
              <div className="flex shrink-0 flex-col justify-center gap-2">
                {CHART_STATUS_ORDER.map((name) => (
                  <div key={name} className="flex items-center gap-2 text-base">
                    <span
                      className="h-3 w-3 shrink-0 rounded-full"
                      style={{
                        backgroundColor: STATUS_COLORS[name] ?? "#94a3b8",
                      }}
                    />
                    <span className="whitespace-nowrap">{name}</span>
                  </div>
                ))}
              </div>

              <div className="mx-auto aspect-square w-[220px] shrink-0 sm:mx-0 sm:w-[260px] md:w-[280px]">
                {pieData.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={pieData}
                        dataKey="value"
                        nameKey="name"
                        cx="50%"
                        cy="50%"
                        outerRadius="82%"
                        paddingAngle={4}
                        cornerRadius={4}
                        stroke="#fff"
                        strokeWidth={2}
                        labelLine={false}
                        label={false}
                      >
                        {pieData.map((entry) => (
                          <Cell
                            key={entry.name}
                            fill={STATUS_COLORS[entry.name] || "#94a3b8"}
                          />
                        ))}
                      </Pie>
                      <Tooltip content={<DefectPieTooltip />} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
                    No status data
                  </div>
                )}
              </div>

              <div className="min-w-0 w-1/2 space-y-4">
                <StatusBarRow
                  statuses={["Reopened", "Resolved", "In Progress"]}
                  countByStatus={countByStatus}
                  maxCount={maxChartCount}
                />
                <StatusBarRow
                  statuses={["Open", "Closed"]}
                  countByStatus={countByStatus}
                  maxCount={maxChartCount}
                />
              </div>
            </div>
          </div>

          <div className="mb-2 px-1">
            <h3 className="h-7 font-display text-[20px] font-bold leading-7 text-[#1F2937]">
              Defects
            </h3>
            <h3 className="font-display text-sm font-normal leading-5 text-[#4B5563]">
              {new Date().toLocaleString("default", {
                month: "long",
                year: "numeric",
              })}
            </h3>
          </div>
          <div className="overflow-auto rounded-md border bg-white">
            {loading ? (
              <div className="p-4">
                <TableSkeleton rows={12} columns={5} rowClassName="py-5" />
              </div>
            ) : (
              <table className="w-full border-collapse bg-white text-sm">
                <thead>
                  <tr className="bg-[#F9F9F8]">
                    <th className={DEFECT_TABLE_HEAD_CELL}>Defect ID</th>
                    <th className={DEFECT_TABLE_HEAD_CELL}>Defect Title</th>
                    <th className={DEFECT_TABLE_HEAD_CELL}>Linked ID</th>
                    <th className={DEFECT_TABLE_HEAD_CELL}>Status</th>
                    <th className={DEFECT_TABLE_HEAD_CELL}>Added By</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {!loading && defects.length === 0 && (
                    <tr className="bg-white">
                      <td
                        className="border-b border-border bg-white p-4 text-muted-foreground"
                        colSpan={5}
                      >
                        No defects found for the selected filters.
                      </td>
                    </tr>
                  )}
                  {defects.map((d) => (
                    <tr key={d.id} className="bg-white hover:bg-gray-50">
                      <td
                        className={`${DEFECT_TABLE_BODY_CELL} font-mono text-xs`}
                      >
                        {d.internal_id || "—"}
                      </td>
                      <td className={DEFECT_TABLE_BODY_CELL}>
                        {d.title || "—"}
                      </td>
                      <td className={DEFECT_TABLE_BODY_CELL}>
                        {d.external_id ? (
                          <a
                            href={getJiraIssueUrl(d.external_id)}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-primary hover:underline inline-flex items-center gap-1"
                          >
                            {d.external_id}
                            <ExternalLink className="h-3 w-3" />
                          </a>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td className={DEFECT_TABLE_BODY_CELL}>
                        <span
                          className="inline-flex rounded px-2 py-0.5 font-sans text-xs font-normal leading-5 opacity-100"
                          style={{
                            backgroundColor: `${STATUS_COLORS[d.status] || "#94a3b8"}20`,
                            color: STATUS_COLORS[d.status] || "#64748b",
                          }}
                        >
                          {d.status || "Open"}
                        </span>
                      </td>
                      <td className={DEFECT_TABLE_BODY_CELL}>
                        {(() => {
                          const reporterName = getDefectReporterName(d);
                          if (!reporterName) return "—";
                          const reporterAvatar = getDefectReporterAvatar(d);
                          return (
                            <div className="flex min-w-0 items-center gap-2">
                              <Avatar className="h-7 w-7 shrink-0">
                                {reporterAvatar ? (
                                  <AvatarImage
                                    src={reporterAvatar}
                                    alt={reporterName}
                                  />
                                ) : null}
                                <AvatarFallback className="border border-border bg-white text-[10px] font-semibold text-[#1C1918]">
                                  {getInitials(reporterName)}
                                </AvatarFallback>
                              </Avatar>
                              <span className="min-w-0 truncate">
                                {reporterName}
                              </span>
                            </div>
                          );
                        })()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </>
      ) : null}
      <PushNewDefectModal
        open={pushModalOpen}
        onClose={() => setPushModalOpen(false)}
        clientId={clientId}
        projectId={projectId}
        onSuccess={handlePushSuccess}
      />
    </div>
  );
}

export default DefectsPage;
